import { useState, useEffect } from "react";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../../ui/sheet";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { toast } from "sonner";
import { Plus, Loader2, Trash2 } from "lucide-react";


const deepCloneWithFiles = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  
  // Preserve File objects
  if (obj instanceof File) return obj;
  
  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => deepCloneWithFiles(item));
  }
  
  // Handle objects
  const cloned = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      cloned[key] = deepCloneWithFiles(obj[key]);
    }
  }
  return cloned;
};

// Schema for quiz question validation
const quizQuestionSchema = z
  .object({
    question: z.string().min(5, "Question is required"),
    options: z
      .array(z.string().min(1, "Option cannot be empty"))
      .min(2, "At least 2 options are required")
      .max(10, "Maximum 10 options allowed"),
    correctAnswer: z.coerce
      .number({
        required_error: "Please select a correct answer",
        invalid_type_error: "Correct answer must be a number",
      })
      .min(0, "Select a valid correct answer"),
    explanation: z.string().optional(),
  })
  .refine((data) => data.correctAnswer < data.options.length, {
    message: "Correct answer must be one of the options",
    path: ["correctAnswer"],
  });

const QuestionEditor = ({
  topics,
  editingQuizMaterial,
  questionBeingEdited,
  setQuestionBeingEdited,
  isQuestionEditorOpen,
  setIsQuestionEditorOpen,
  isEditing,
  setIsEditing,
  onUpdateTopics, // Function to update topics in parent
}) => {
  const [isSaving, setIsSaving] = useState(false);
  console.log(questionBeingEdited)

  // Initialize form for question editor
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(quizQuestionSchema),
    defaultValues: {
      question: "",
      options: ["", "", "", ""],
      correctAnswer: 0,
      explanation: "",
    },
  });

  // Add a new option
  const addOption = () => {
    const currentOptions = watch("options") || [];
    if (currentOptions.length < 10) {
      setValue("options", [...currentOptions, ""], { shouldValidate: false });
    }
  };

  // Remove an option
  const removeOption = (indexToRemove) => {
    const currentOptions = watch("options") || [];
    const currentAnswer = watch("correctAnswer");

    const newOptions = currentOptions.filter(
      (_, index) => index !== indexToRemove
    );

    // Adjust the correct answer if needed
    let newAnswer = currentAnswer;
    if (currentAnswer === indexToRemove) {
      newAnswer = 0; // Reset to first option if removed option was correct
    } else if (currentAnswer > indexToRemove) {
      newAnswer = currentAnswer - 1; // Adjust index if needed
    }

    setValue("options", newOptions, { shouldValidate: false });
    setValue("correctAnswer", newAnswer, { shouldValidate: false });
  };

  // Close question editor and reset form
  const closeQuestionEditor = () => {
    setIsQuestionEditorOpen(false);
    setQuestionBeingEdited(null);
    setIsEditing(false);
    reset();
  };

  // Set up form with editing question data when it changes
  useEffect(() => {
    if (questionBeingEdited) {
      if (questionBeingEdited.id) {
        setIsEditing(true);
        const options = Array.isArray(questionBeingEdited.options)
          ? questionBeingEdited.options.map((opt) =>
              typeof opt === "object" ? opt.text : opt
            )
          : ["", "", "", ""];

        setValue("question", questionBeingEdited.question || "");
        setValue("options", options);

        // Handle both old (number) and new (object) correctAnswer format
        let correctAnswerIndex = 0;
        if (questionBeingEdited.correctAnswer) {
          if (typeof questionBeingEdited.correctAnswer === "object") {
            // New format: {id, text}
            const correctId = questionBeingEdited.correctAnswer.id;
            correctAnswerIndex = options.findIndex(
              (_, idx) => String(idx) === correctId
            );
            if (correctAnswerIndex === -1) correctAnswerIndex = 0;
          } else {
            // Old format: number (index)
            correctAnswerIndex = Number(questionBeingEdited.correctAnswer) || 0;
          }
        }
        setValue("correctAnswer", correctAnswerIndex);
        setValue("explanation", questionBeingEdited.explanation || "");
      } else {
        setIsEditing(false);
        reset({
          question: "",
          options: ["", "", "", ""],
          correctAnswer: 0,
          explanation: "",
        });
      }
    } else {
      setIsEditing(false);
      reset();
    }
  }, [questionBeingEdited, reset, setValue, setIsEditing]);

  // Set the correct answer to the first option if it's a new question
  useEffect(() => {
    if (!questionBeingEdited?.id) {
      setValue("correctAnswer", 0);
    }
  }, [questionBeingEdited?.id, setValue]);

  // Handle form submission for adding/editing a question
  const onSubmitQuestion = async (data) => {
    try {
      setIsSaving(true);

      if (!editingQuizMaterial) {
        console.error("No quiz material context found");
        toast.error("Error: Missing quiz context");
        return;
      }

      // Get the current topic and material indices
      const topicIndex = editingQuizMaterial.topicIndex;
      const materialIndex = editingQuizMaterial.materialIndex;

      // Ensure we have valid indices
      if (
        topicIndex === undefined ||
        materialIndex === undefined ||
        !topics[topicIndex] ||
        !topics[topicIndex].learningMaterials[materialIndex]
      ) {
        console.error("Invalid topic or material index");
        toast.error("Error: Invalid quiz context");
        return;
      }
      // Get the target question set index from the editing question or use the first set as default
      const targetQuestionSetIndex =
        typeof questionBeingEdited?.quizSetIndex === "number"
          ? questionBeingEdited.quizSetIndex
          : 0;

      const updatedTopics = deepCloneWithFiles(topics);
      const material = updatedTopics[topicIndex].learningMaterials[materialIndex];

      // Initialize test data if it doesn't exist (using 'test' instead of 'quiz' to match backend schema)
      if (!material.test || !Array.isArray(material.test)) {
        material.test = [];
      }

      // If no tests exist, create the first one
      if (material.test.length === 0) {
        material.test.push({
          id: `test-${Date.now()}`,
          title: `Quiz for ${material.title || "Untitled Material"}`,
          timelimit: 0,
          type: "QUIZ",
          questionSets: [],
          questions: [],
        });
      }

      // Get the first test (we're working with the first test for now)
      const test = material.test[0];

      // Ensure questionSets exists and is an array
      if (!Array.isArray(test.questionSets)) {
        test.questionSets = [];
      }

      // If no question sets exist, create the first one
      if (test.questionSets.length === 0) {
        test.questionSets.push({
          id: `set-1`,
          title: "Set 1",
          questions: [],
        });
      }

      // Ensure the target set exists
      if (!test.questionSets[targetQuestionSetIndex]) {
        test.questionSets[targetQuestionSetIndex] = {
          id: `set-${targetQuestionSetIndex + 1}`,
          title: `Set ${targetQuestionSetIndex + 1}`,
          questions: [],
        };
      }

      // Get the target question set
      const questionSet = test.questionSets[targetQuestionSetIndex];

      // Ensure questions array exists
      if (!Array.isArray(questionSet.questions)) {
        questionSet.questions = [];
      }
      // Create the question object
      const question = {
        id: questionBeingEdited?.id || `q-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        question: data.question,
        options: data.options.map((text, index) => ({
          id: String(index),
          text: text,
        })),
        correctAnswer: {
          id: String(data.correctAnswer),
          text: data.options[data.correctAnswer] || "",
        },
        explanation: data.explanation || "",
        // Store the questionSetIndex with the question for reference
        questionSetIndex: targetQuestionSetIndex,
        // Store the set title for display purposes
        setTitle: questionSet.title || `Set ${targetQuestionSetIndex + 1}`,
      };

      if (questionBeingEdited?.id) {
        // Update existing question
        const questionIndex = questionSet.questions.findIndex(
          (q) => q.id === questionBeingEdited.id
        );
        if (questionIndex !== -1) {
          questionSet.questions[questionIndex] = question;
        } else {
          // If question not found in the current set, add it as new
          questionSet.questions.push(question);
        }
      } else {
        // Add new question to the target set
        questionSet.questions.push(question);
      }

      // Update the topics using the callback
      if (onUpdateTopics) {
        onUpdateTopics(updatedTopics);
      }

      closeQuestionEditor();
      toast.success(
        isEditing
          ? "Question updated successfully"
          : "Question added successfully"
      );

      // Log the question data for debugging
      console.log('Question saved:', {
        materialId: material.id,
        testId: test.id,
        questionSetId: questionSet.id,
        question: question
      });

    } catch (error) {
      console.error("Error saving question:", error);
      toast.error("Failed to save question");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Sheet
      open={isQuestionEditorOpen}
      onOpenChange={(open) => {
        if (!open) closeQuestionEditor();
        else setIsQuestionEditorOpen(open);
      }}
    >
      <SheetContent
        side="right"
        className="w-screen sm:max-w-[40vw] overflow-y-auto px-10 z-[100]"
        onInteractOutside={(e) => {
          // Prevent closing when clicking on the sheet content
          if (e.target.closest(".sheet-content")) {
            e.preventDefault();
          }
        }}
      >
        <div className="sheet-content w-full h-full flex flex-1 flex-col">
          <SheetHeader className="my-4 p-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center justify-start gap-2">
                <SheetTitle className="text-[var(--secondary-font)] font-bold text-lg">
                  {isEditing ? "Edit Question" : "Add New Question"}
                </SheetTitle>
                {questionBeingEdited?.setTitle && (
                  <Badge variant="outline" className="text-xs font-medium">
                    {questionBeingEdited.setTitle}
                  </Badge>
                )}
              </div>
            </div>
            <SheetDescription className="text-[var(--secondary-font)]/80 font-light text-sm">
              {isEditing
                ? "Update the question and options below."
                : "Fill in the question and options below."}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 py-6">
            <form
              onSubmit={handleSubmit(onSubmitQuestion)}
              className="space-y-6"
            >
              <div className="space-y-2">
                <Label htmlFor="question">Question</Label>
                <Textarea
                  id="question"
                  placeholder="Enter the question"
                  {...register("question")}
                  className="min-h-[100px]"
                />
                {errors.question && (
                  <p className="text-sm font-medium text-red-800">
                    {errors.question.message}
                  </p>
                )}
              </div>

              <div className="space-y-4">
                <Label>Options</Label>
                <div className="space-y-3">
                  <RadioGroup
                    onValueChange={(value) => {
                      setValue("correctAnswer", parseInt(value, 10), {
                        shouldValidate: true,
                      });
                    }}
                    value={watch("correctAnswer")?.toString() || "0"}
                    className="space-y-3"
                  >
                    {(watch("options") || []).map((_, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="flex items-center h-10">
                          <RadioGroupItem
                            value={index.toString()}
                            id={`option-${index}`}
                            className="text-[var(--primary-font)] border-muted-foreground/50 mt-1"
                          />
                        </div>
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <Input
                              id={`option-${index}-text`}
                              placeholder={`Option ${index + 1}`}
                              {...register(`options.${index}`, {
                                required: "Option cannot be empty",
                                validate: (value) =>
                                  value.trim() !== "" ||
                                  "Option cannot be empty",
                              })}
                              className="flex-1"
                            />
                            {(watch("options")?.length || 0) > 2 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                onClick={(e) => {
                                  e.preventDefault();
                                  removeOption(index);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          {errors.options?.[index] && (
                            <p className="text-sm font-medium text-red-800">
                              {errors.options[index].message}
                            </p>
                          )}
                          {errors.correctAnswer?.type === "custom" &&
                            index === 0 && (
                              <p className="text-sm font-medium text-red-800">
                                {errors.correctAnswer.message}
                              </p>
                            )}
                        </div>
                      </div>
                    ))}
                  </RadioGroup>

                  {(watch("options")?.length || 0) < 10 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={addOption}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Option
                    </Button>
                  )}
                </div>
                {errors.correctAnswer && (
                  <p className="text-sm font-medium text-red-800">
                    {errors.correctAnswer.message}
                  </p>
                )}
              </div>

              <SheetFooter className="pt-4">
                <div className="flex justify-end space-x-3 w-full">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={closeQuestionEditor}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSaving}
                    className="min-w-[120px]"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {isEditing ? "Updating..." : "Adding..."}
                      </>
                    ) : isEditing ? (
                      "Update Question"
                    ) : (
                      "Add Question"
                    )}
                  </Button>
                </div>
              </SheetFooter>
            </form>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default QuestionEditor;